/**
 * The integration API, typed at the edge of this package.
 *
 * Hand-written rather than pulled from the generated OpenAPI types, for the
 * reason the other features give: the generated schema is the authority and CI
 * fails on drift, but reaching into `components["schemas"][...]` at every call
 * site makes the code unreadable.
 *
 * Two things to notice, because they are the contract rather than the code:
 *
 * **No ERP appears in any type or any URL.** There is no `SageSettings`, no
 * `/sage/…`. A connector is a `code`, a label and a free-shaped `settings`
 * document whose shape belongs to the connector — which is what makes adding
 * one a configuration change rather than a release.
 *
 * **A token appears exactly once.** `MintedAccount` is the only type in this
 * package that carries one, returned by the request that created it. There is
 * no method that reads one back, because there is no endpoint that could.
 */

import type { ApiClient } from "@hardauto/core-api";

const BASE = "/api/v1/integration";

/**
 * Who may write a field, and which way it travels.
 *
 * The four values of the mapping screen's `Sens` column, and the whole reason
 * that column exists: an installation decides, per entity and per field,
 * whether the ERP or the portal owns it.
 */
export type Direction = "in" | "out" | "both" | "local";

/** How one synchronisation attempt ended. */
export type SyncOutcome = "ok" | "unchanged" | "refused" | "failed";

export type QueueState = "to_publish" | "published" | "failed";

/** The declarative transformation catalogue. Closed: a mapping is configuration. */
export type TransformKind =
  | "none"
  | "trim"
  | "upper"
  | "lower"
  | "title"
  | "slug"
  | "date"
  | "decimal"
  | "scale"
  | "affix"
  | "value_map"
  | "boolean";

export type WebhookEvent =
  "order.placed" | "order.approved" | "order.cancelled" | "shipment.recorded" | "account.requested";

/**
 * A webhook event's translation key.
 *
 * The wire values carry a dot, and a dot is i18next's key separator — so
 * `t("event.order.placed")` would look for a nested `order` object and render
 * the raw key to the reader. The event names belong to the backend and are not
 * ours to change; the keys are, so the mapping lives here rather than a
 * `replace` at each call site.
 */
export const EVENT_KEYS: Record<WebhookEvent, string> = {
  "order.placed": "orderPlaced",
  "order.approved": "orderApproved",
  "order.cancelled": "orderCancelled",
  "shipment.recorded": "shipmentRecorded",
  "account.requested": "accountRequested",
};

export interface EntityContract {
  code: string;
  label: string;
  direction: Direction;
  is_enabled: boolean;
  external_key_field: string;
  /** How many mapping rows it has. Zero means the payload passes through. */
  field_count: number;
  /**
   * Whether anything can write it from a synchronisation. `order` travels
   * outward only, and the screen has to be able to say so rather than
   * offering a direction that cannot work.
   */
  is_writable: boolean;
  last_inbound_at: string | null;
  last_outbound_at: string | null;
}

export interface FieldMapping {
  id: string;
  entity: string;
  external_field: string;
  portal_field: string;
  direction: Direction;
  transform: TransformKind;
  transform_argument: string;
  position: number;
  is_active: boolean;
  /** True when the row deliberately drops an arriving field. */
  is_dropped: boolean;
}

export interface FieldMappingList {
  items: FieldMapping[];
  /** What a sink for this entity accepts, so the screen offers a select. */
  portal_fields: string[];
}

export interface JournalEntry {
  id: string;
  at: string;
  entity: string;
  way: string;
  outcome: SyncOutcome;
  external_system: string;
  external_ref: string;
  portal_id: string | null;
  message: string;
  written_fields: string[];
  refused_fields: string[];
  is_anomaly: boolean;
}

export interface JournalPage {
  items: JournalEntry[];
  total: number;
  next_cursor: string | null;
}

export interface QueuedChange {
  id: string;
  entity: string;
  portal_id: string;
  operation: string;
  state: QueueState;
  attempts: number;
  last_error: string;
  external_ref: string;
  created_at: string | null;
  published_at: string | null;
  /** False past the retry ceiling — the screen's cue to offer a replay. */
  may_retry: boolean;
  payload_size: number;
}

export interface QueueList {
  items: QueuedChange[];
  pending: number;
  failed: number;
}

export interface Connector {
  code: string;
  label: string;
  /**
   * The connector's own shape. Credential values come back as `MASKED`;
   * submitting that value back leaves the stored one alone.
   */
  settings: Record<string, unknown>;
  is_enabled: boolean;
  updated_at: string | null;
  /** True when no connector is configured — a complete state, not an error. */
  is_absent: boolean;
}

/** What a masked credential reads as, and what sending it back means. */
export const MASKED = "••••••";

export interface ServiceAccount {
  id: string;
  name: string;
  token_prefix: string;
  entities: string[];
  is_active: boolean;
  last_used_at: string | null;
  created_at: string | null;
}

export interface MintedAccount {
  account: ServiceAccount;
  /** Shown once. Stored as a hash. No endpoint returns it again. */
  token: string;
  notice: string;
}

export interface Webhook {
  id: string;
  url: string;
  events: WebhookEvent[];
  is_active: boolean;
  last_delivery_at: string | null;
  last_status: number | null;
  failure_count: number;
}

export interface Lock {
  entity: string;
  portal_id: string;
  locked: string[];
  lockable: string[];
}

export interface OutcomeCount {
  outcome: SyncOutcome;
  count: number;
}

export interface Overview {
  connector: Connector;
  entities: EntityContract[];
  outcomes: OutcomeCount[];
  queue_pending: number;
  queue_failed: number;
  anomalies_24h: number;
}

export interface JournalQuery {
  entity?: string;
  way?: "in" | "out";
  outcome?: SyncOutcome;
  anomalies_only?: boolean;
  after_id?: string;
  limit?: number;
}

function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "" || value === false) continue;
    search.set(key, String(value));
  }
  const rendered = search.toString();
  return rendered ? `?${rendered}` : "";
}

/** Every integration call, in one place. */
export class IntegrationApi {
  readonly #client: ApiClient;

  constructor(client: ApiClient) {
    this.#client = client;
  }

  // --- The supervision screen ---------------------------------------------

  /**
   * Connector, entities, outcome counts and queue depth in one request.
   *
   * One request rather than six, because this is a screen that is left open on
   * a wall: six polls per interval is how a supervision page becomes the
   * busiest client of its own API.
   */
  overview(): Promise<Overview> {
    return this.#client.request<Overview>(`${BASE}/overview`);
  }

  // --- The contract --------------------------------------------------------

  listEntities(): Promise<{ items: EntityContract[] }> {
    return this.#client.request<{ items: EntityContract[] }>(`${BASE}/entities`);
  }

  updateEntity(
    code: string,
    body: Partial<{
      label: string;
      direction: Direction;
      is_enabled: boolean;
      external_key_field: string;
    }>,
  ): Promise<EntityContract> {
    return this.#client.request<EntityContract>(`${BASE}/entities/${encodeURIComponent(code)}`, {
      method: "PATCH",
      body,
    });
  }

  // --- The mapping ---------------------------------------------------------

  listFields(entity: string, includeInactive = true): Promise<FieldMappingList> {
    return this.#client.request<FieldMappingList>(
      `${BASE}/entities/${encodeURIComponent(entity)}/fields${query({
        include_inactive: includeInactive,
      })}`,
    );
  }

  addField(
    entity: string,
    body: {
      external_field: string;
      portal_field?: string;
      direction?: Direction;
      transform?: TransformKind;
      transform_argument?: string;
      position?: number;
    },
  ): Promise<FieldMapping> {
    return this.#client.request<FieldMapping>(
      `${BASE}/entities/${encodeURIComponent(entity)}/fields`,
      { method: "POST", body },
    );
  }

  updateField(
    id: string,
    body: Partial<{
      portal_field: string;
      direction: Direction;
      transform: TransformKind;
      transform_argument: string;
      position: number;
      is_active: boolean;
    }>,
  ): Promise<FieldMapping> {
    return this.#client.request<FieldMapping>(`${BASE}/fields/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body,
    });
  }

  removeField(id: string): Promise<void> {
    return this.#client.request<void>(`${BASE}/fields/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  }

  // --- Locks ---------------------------------------------------------------

  locks(entity: string, portalId: string): Promise<Lock> {
    return this.#client.request<Lock>(
      `${BASE}/locks/${encodeURIComponent(entity)}/${encodeURIComponent(portalId)}`,
    );
  }

  setLocks(entity: string, portalId: string, fields: readonly string[]): Promise<Lock> {
    return this.#client.request<Lock>(
      `${BASE}/locks/${encodeURIComponent(entity)}/${encodeURIComponent(portalId)}`,
      { method: "PUT", body: { fields: [...fields] } },
    );
  }

  // --- The journal ---------------------------------------------------------

  journal(options: JournalQuery = {}): Promise<JournalPage> {
    return this.#client.request<JournalPage>(
      `${BASE}/journal${query({ ...options, limit: options.limit ?? 50 })}`,
    );
  }

  // --- The queue -----------------------------------------------------------

  queue(options: { state?: QueueState; entity?: string; limit?: number } = {}): Promise<QueueList> {
    return this.#client.request<QueueList>(`${BASE}/queue${query(options)}`);
  }

  replay(id: string): Promise<QueuedChange> {
    return this.#client.request<QueuedChange>(`${BASE}/queue/${encodeURIComponent(id)}/replay`, {
      method: "POST",
    });
  }

  // --- The connector -------------------------------------------------------

  connector(): Promise<Connector> {
    return this.#client.request<Connector>(`${BASE}/connector`);
  }

  /** Connector codes this build carries. Empty on a portal with no ERP. */
  availableConnectors(): Promise<string[]> {
    return this.#client.request<string[]>(`${BASE}/connector/available`);
  }

  saveConnector(body: {
    code: string;
    label: string;
    settings: Record<string, unknown>;
    is_enabled: boolean;
  }): Promise<Connector> {
    return this.#client.request<Connector>(`${BASE}/connector`, { method: "PUT", body });
  }

  // --- Machine credentials -------------------------------------------------

  listAccounts(): Promise<{ items: ServiceAccount[] }> {
    return this.#client.request<{ items: ServiceAccount[] }>(`${BASE}/accounts`);
  }

  mintAccount(body: { name: string; entities?: readonly string[] }): Promise<MintedAccount> {
    return this.#client.request<MintedAccount>(`${BASE}/accounts`, {
      method: "POST",
      body: { name: body.name, entities: [...(body.entities ?? [])] },
    });
  }

  revokeAccount(id: string): Promise<ServiceAccount> {
    return this.#client.request<ServiceAccount>(`${BASE}/accounts/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  }

  // --- Webhooks ------------------------------------------------------------

  listWebhooks(): Promise<{ items: Webhook[] }> {
    return this.#client.request<{ items: Webhook[] }>(`${BASE}/webhooks`);
  }

  addWebhook(body: {
    url: string;
    secret: string;
    events: readonly WebhookEvent[];
  }): Promise<Webhook> {
    return this.#client.request<Webhook>(`${BASE}/webhooks`, {
      method: "POST",
      body: { url: body.url, secret: body.secret, events: [...body.events] },
    });
  }

  removeWebhook(id: string): Promise<void> {
    return this.#client.request<void>(`${BASE}/webhooks/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  }
}
